import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChatCircleDots, X, PaperPlaneTilt } from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatWidget = () => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/chat`, formData);
      setSubmitted(true);
      toast.success('Message sent! We\'ll get back to you soon.');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', message: '' });
    }, 300);
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        data-testid="chat-widget-btn"
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-24 right-6 z-[9999] w-14 h-14 rounded-full flex items-center justify-center shadow-lg chat-widget-btn ${
          isOpen ? 'scale-0' : 'scale-100'
        } transition-transform duration-300`}
        style={{ 
          background: 'linear-gradient(135deg, #464B8B 0%, #00C8EB 100%)',
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <ChatCircleDots size={28} weight="duotone" className="text-white" />
      </motion.button>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            data-testid="chat-widget-panel"
            className={`fixed bottom-24 right-6 z-[9999] w-[350px] max-w-[calc(100vw-48px)] rounded-2xl overflow-hidden shadow-2xl ${
              theme === 'dark'
                ? 'bg-[#12131F] border border-[#464B8B]/30'
                : 'bg-white border border-gray-200'
            }`}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#464B8B] to-[#00C8EB] p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <ChatCircleDots size={24} weight="duotone" className="text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Chat with Us</h3>
                  <p className="text-white/70 text-sm">We typically reply within minutes</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                data-testid="chat-close-btn"
                className="text-white/80 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-8"
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#00C8EB]/20 flex items-center justify-center">
                    <PaperPlaneTilt size={32} weight="duotone" className="text-[#00C8EB]" />
                  </div>
                  <h4 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Message Sent!
                  </h4>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    Thanks for reaching out. We'll get back to you shortly.
                  </p>
                  <Button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: '', email: '', message: '' });
                    }}
                    data-testid="chat-new-message-btn"
                    className="mt-4 bg-[#464B8B] hover:bg-[#464B8B]/90"
                  >
                    Send Another Message
                  </Button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="chat-name-input"
                      className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30' : ''}
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      placeholder="Your email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="chat-email-input"
                      className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30' : ''}
                    />
                  </div>
                  <div>
                    <Textarea
                      placeholder="How can we help you?"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      data-testid="chat-message-input"
                      rows={4}
                      className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30 resize-none' : 'resize-none'}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    data-testid="chat-submit-btn"
                    className="w-full bg-[#00C8EB] hover:bg-[#00C8EB]/90 text-[#0A0A10] font-semibold"
                  >
                    {isLoading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
